import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import './Auth.css';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useView } from '../../../context/ViewContext';
import { handleAuthSuccess } from '../../../utils/auth';
import { trackEvent } from '../../../utils/analytics';
import { clearStoredSignupMode } from '../../../utils/signupMode';
import { clearSignupCheckout, getSignupCheckout } from '../../../utils/signupCheckout';
import { getSupportedBrowserTimeZone } from '../../../utils/timeZones';

const COMPLETION_RETRY_CODES = new Set([
  'SIGNUP_COMPLETION_IN_PROGRESS',
  'SIGNUP_ALREADY_COMPLETED',
]);
// The account exists, but this request may not sign into it: the session_id
// came without the checkout tab's signup link, or that tab was already signed
// back in once, or too long ago.
const LOGIN_REQUIRED_CODE = 'SIGNUP_COMPLETED_LOGIN_REQUIRED';
const MAX_COMPLETION_ATTEMPTS = 12;

const getCompletionError = (error) => {
  const payload = error?.response?.data || {};
  const nestedError = payload?.error && typeof payload.error === 'object'
    ? payload.error
    : null;
  return {
    code: payload.code || nestedError?.code || '',
    message:
      (typeof payload.error === 'string' ? payload.error : nestedError?.message) ||
      payload.message ||
      error?.message ||
      'Unable to finalize your signup.',
    retryAfterMs: Math.min(Math.max(Number(payload.retryAfterMs) || 500, 250), 4000),
  };
};

const completeSignup = async (sessionId) => {
  // If another request finishes the signup first, the server only signs this
  // tab in when it also sends the signup link SignupCheckout kept for it.
  // The zone is a fallback for pending signups saved before the signup form
  // sent one; the server prefers the zone captured with the form.
  const payload = {
    sessionId,
    timeZone: getSupportedBrowserTimeZone(),
    ...getSignupCheckout(sessionId),
  };
  for (let completionAttempt = 0; completionAttempt < MAX_COMPLETION_ATTEMPTS; completionAttempt += 1) {
    try {
      return await axios.post('/api/auth/signup', payload);
    } catch (completionError) {
      const apiError = getCompletionError(completionError);
      const shouldRetry =
        COMPLETION_RETRY_CODES.has(apiError.code) &&
        completionAttempt < MAX_COMPLETION_ATTEMPTS - 1;
      if (!shouldRetry) throw completionError;

      const backoffMs = Math.min(500 * (2 ** completionAttempt), 4000);
      await new Promise((resolve) => {
        setTimeout(resolve, Math.max(apiError.retryAfterMs, backoffMs));
      });
    }
  }

  throw new Error('Unable to restore your completed signup.');
};

const SignupSuccess = () => {
  const navigate = useNavigate();
  const { setCurrentView } = useView();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  // React Strict Mode replays effects; both runs should observe the same completion request.
  const completionRequestRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setError('Signup session not found. Please restart the process.');
      setStatus('error');
      return;
    }

    let isMounted = true;

    // Stripe redirects the user here with a session_id once payment/onboarding succeeds.
    // We exchange it for an application token so the rest of the auth flow can run.
    const finalizeSignup = async () => {
      let activeRequest = null;
      try {
        setStatus('loading');
        setError('');
        if (
          !completionRequestRef.current ||
          completionRequestRef.current.sessionId !== sessionId
        ) {
          completionRequestRef.current = {
            sessionId,
            request: completeSignup(sessionId),
          };
        }
        activeRequest = completionRequestRef.current;
        const response = await activeRequest.request;

        const { token, foundingStylist, user } = response.data || {};
        const foundingActive = Boolean(foundingStylist?.active);
        const foundingTrialEndsAt = foundingStylist?.trialEndsAt || null;
        const foundingTrialDays = foundingStylist?.trialDays ?? null;
        if (!token) {
          throw new Error('We couldn’t open your workspace. Please try again.');
        }

        if (!isMounted) return;

        const redirectPath = user?.activeBusiness?.accountMode === 'team'
          ? '/onboarding/team'
          : '/onboarding';
        clearStoredSignupMode();
        clearSignupCheckout();
        handleAuthSuccess({ token, navigate, setCurrentView, redirectPath, user });
        if (foundingActive) {
          trackEvent('signup_promo_conversion', {
            status: 'activated',
            source: 'checkout_success',
            foundingStylist: foundingActive,
            trialEndsAt: foundingTrialEndsAt,
            trialDays: foundingTrialDays,
          });
        }
        setStatus('success');
      } catch (err) {
        if (!isMounted) return;

        const apiError = getCompletionError(err);
        if (apiError.code === LOGIN_REQUIRED_CODE) {
          setStatus('login-required');
          return;
        }
        setError(apiError.message);
        setStatus('error');
      } finally {
        if (completionRequestRef.current === activeRequest) {
          completionRequestRef.current = null;
        }
      }
    };

    finalizeSignup();

    return () => {
      isMounted = false;
    };
  }, [attempt, sessionId, navigate, setCurrentView]);

  if (status === 'loading') {
    return (
      <div className="auth-page">
        <div className="auth-inner">
          <section className="auth-card">
            <h1>Setting up your account…</h1>
            <p>We&apos;re finalizing your Haastia account. This only takes a moment.</p>
          </section>
        </div>
      </div>
    );
  }

  if (status === 'login-required') {
    return (
      <div className="auth-page">
        <div className="auth-inner">
          <section className="auth-card">
            <h1>Your account is ready</h1>
            <p>Log in to continue.</p>
            {/* The site's .btn classes, not .auth-submit: in the public site
                shell `.site a` outranks .auth-submit and would turn the link
                text rust on a rust background. */}
            <Link className="btn btn-primary btn-lg btn-block" to="/login">
              Log in
            </Link>
          </section>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="auth-page">
        <div className="auth-inner">
          <section className="auth-card">
            <h1>We couldn&apos;t finish signup</h1>
            <p>{error}</p>
            <button className="auth-submit" type="button" onClick={() => setAttempt((value) => value + 1)}>
              Try completion again
            </button>
            <button className="auth-submit auth-submit--secondary" type="button" onClick={() => navigate('/login')}>
              Sign in
            </button>
          </section>
        </div>
      </div>
    );
  }

  // When handleAuthSuccess navigates away we rarely render this, but it helps avoid a flash.
  return (
    <div className="auth-page">
      <div className="auth-inner">
        <section className="auth-card">
          <h1>Redirecting…</h1>
          <p>You&apos;re all set! We&apos;re taking you to your new workspace.</p>
        </section>
      </div>
    </div>
  );
};

export default SignupSuccess;
